import type { SupabaseClient } from '@supabase/supabase-js';

const REPOSITORY = /^[a-z0-9_.-]{1,100}\/[a-z0-9_.-]{1,100}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CONTROL = /[\u0000-\u001f\u007f]/;
const REF_FORBIDDEN = /[\u0000- \u007f~^:?*\[\]\\]/;

export interface ProjectRegistrationRequest {
  readonly orgId: string;
  readonly projectName: string;
  readonly repositoryName: string;
  readonly defaultRef: string;
  readonly requestId: string;
}

/** One existing project, named by its ID, receiving its first repository. */
export interface ExistingProjectRepositoryRequest {
  readonly orgId: string;
  readonly projectId: string;
  readonly repositoryName: string;
  readonly defaultRef: string;
  readonly requestId: string;
}

export interface RegisteredProjectRepository {
  readonly projectId: string;
  readonly projectName: string;
  readonly repositoryId: string;
  readonly repositoryName: string;
  readonly defaultRef: string;
}

function validRef(value: string): boolean {
  if (value.length < 1 || value.length > 250 || REF_FORBIDDEN.test(value)
    || value.startsWith('/') || value.startsWith('-') || value.endsWith('/')
    || value.includes('..') || value.includes('//') || value.includes('@{')) return false;
  return value.split('/').every((part) => part !== '' && part !== '@'
    && !part.startsWith('.') && !part.endsWith('.') && !part.endsWith('.lock'));
}

/** The exact five-key request accepted by the guarded registration RPC. */
export function projectRegistrationRequest(input: {
  readonly orgId: string;
  readonly projectName: string;
  readonly repositoryName: string;
  readonly defaultRef: string;
  readonly requestId: string;
}): ProjectRegistrationRequest | null {
  const projectName = input.projectName.trim();
  const repositoryName = input.repositoryName.trim().toLowerCase();
  const defaultRef = input.defaultRef.trim();
  if (projectName.length < 1 || projectName.length > 120 || CONTROL.test(projectName)
    || !REPOSITORY.test(repositoryName) || !validRef(defaultRef) || !UUID.test(input.requestId)) return null;
  return { orgId: input.orgId, projectName, repositoryName, defaultRef, requestId: input.requestId };
}

/** The exact five-key request that gives one existing project its first repository. */
export function existingProjectRepositoryRequest(input: {
  readonly orgId: string;
  readonly projectId: string;
  readonly repositoryName: string;
  readonly defaultRef: string;
  readonly requestId: string;
}): ExistingProjectRepositoryRequest | null {
  const repositoryName = input.repositoryName.trim().toLowerCase();
  const defaultRef = input.defaultRef.trim();
  if (input.projectId === '' || !REPOSITORY.test(repositoryName) || !validRef(defaultRef)
    || !UUID.test(input.requestId)) return null;
  return { orgId: input.orgId, projectId: input.projectId, repositoryName, defaultRef, requestId: input.requestId };
}

const MESSAGES: Readonly<Record<string, string>> = {
  forbidden: 'Owner verification is required to register a project and repository.',
  unauthorized: 'Sign in again to register a project and repository.',
  invalid_input: 'Enter a project name, an exact owner/repository identifier, and a valid default branch.',
  conflict: 'That request ID was already used for different registration details.',
  project_name_conflict: 'That project name or reference already exists. To give an existing project its first repository, use "Register a repository for this project" under its name.',
  repository_name_conflict: 'That repository identifier already exists in this organization.',
  ambiguous_existing_state: 'Existing project and repository identities conflict with this registration. Nothing was changed.',
};

const EXISTING_PROJECT_MESSAGES: Readonly<Record<string, string>> = {
  ...MESSAGES,
  forbidden: 'Owner verification is required to register a repository for this project.',
  unauthorized: 'Sign in again to register a repository for this project.',
  invalid_input: 'Enter an exact owner/repository identifier and a valid default branch.',
  not_found: 'That project is no longer in this organization. Nothing was changed.',
  project_has_repository: 'This project already has a repository. A registered repository is never replaced.',
};

const UNKNOWN = 'Brunel could not confirm the registration. Retry uses the same request ID, so it cannot create a duplicate.';

export type ProjectRegistrationOutcome = 'saved' | 'rejected' | 'unknown';

export interface ProjectRegistrationResult {
  readonly outcome: ProjectRegistrationOutcome;
  readonly message: string | null;
  readonly value: RegisteredProjectRepository | null;
}

async function saveOnce(
  client: Pick<SupabaseClient, 'rpc'>,
  rpc: 'register_project_repository' | 'register_repository_for_project',
  request: ProjectRegistrationRequest | ExistingProjectRepositoryRequest,
  messages: Readonly<Record<string, string>>,
  rejected: string,
): Promise<ProjectRegistrationResult> {
  let data: unknown;
  try {
    const response = await client.rpc(rpc, { request });
    if (response.error) return { outcome: 'unknown', message: UNKNOWN, value: null };
    data = response.data;
  } catch {
    return { outcome: 'unknown', message: UNKNOWN, value: null };
  }
  const result = data as { ok?: boolean; value?: RegisteredProjectRepository; error?: { code?: string } } | null;
  if (result?.ok === true && result.value) return { outcome: 'saved', message: null, value: result.value };
  const code = result?.error?.code ?? 'internal';
  return { outcome: 'rejected', message: messages[code] ?? rejected, value: null };
}

/** One network attempt. The caller retains this request so an unknown result retries its ID. */
export async function saveProjectRegistrationOnce(
  client: Pick<SupabaseClient, 'rpc'>,
  request: ProjectRegistrationRequest,
): Promise<ProjectRegistrationResult> {
  return saveOnce(client, 'register_project_repository', request, MESSAGES,
    'The project and repository registration was rejected.');
}

/** One network attempt at an existing project's first repository; the same retry rule applies. */
export async function saveExistingProjectRepositoryOnce(
  client: Pick<SupabaseClient, 'rpc'>,
  request: ExistingProjectRepositoryRequest,
): Promise<ProjectRegistrationResult> {
  return saveOnce(client, 'register_repository_for_project', request, EXISTING_PROJECT_MESSAGES,
    'The repository registration was rejected.');
}
